/**
 * Shared helpers for opening International (HUIDU) casino games.
 * Must match casinoOther.tsx launch payload (lobby_url = HUIDU game_uid / game_identifier).
 * Same pattern as frontend/src/utils/casinoGameLaunch.ts (Lobby GAMES).
 */

export type CasinoOpenGame = {
  _id: string
  identifier: string
  game_name: string
  game_images?: string
  game_provider?: string
  game_slot_status?: boolean
  openProviderLobby?: boolean
  /** Evolution lobby SPA hash category, e.g. all_games | baccarat | game_shows */
  evoCategory?: string | null
}

export function resolveCasinoGameUid(game: Record<string, unknown> | null | undefined): string {
  if (!game) return ''
  const raw =
    game.identifier ??
    game.game_identifier ??
    game._id ??
    game.id
  return raw != null ? String(raw).trim() : ''
}

/** slots vs live-casino — must match backend getCasinoIntList type filters. */
export function inferCasinoRouteType(game: Record<string, unknown> | null | undefined): string {
  if (!game) return 'slots'
  if (game.game_slot_status === true) return 'slots'
  if (game.game_slot_status === false) return 'live-casino'

  const name = String(game.game_name ?? '').toLowerCase()
  const provider = String(game.game_provider ?? game.provider_id ?? '').toLowerCase()

  if (/(evolution|ezugi|sexy|sa gaming|live casino|live|playtech|km|king\s?midas)/i.test(provider)) {
    return 'live-casino'
  }
  if (/(aviator|crash|spribe)/i.test(name) || /(aviator|crash|spribe)/i.test(provider)) {
    return 'fantasy'
  }
  if (/(virtual|bollywood|lucky 7|dtl)/i.test(name)) {
    return 'virtual-casino'
  }
  return 'slots'
}

export function buildCasinoOpenPayload(game: Record<string, unknown>): CasinoOpenGame | null {
  const identifier = resolveCasinoGameUid(game)
  if (!identifier) return null

  const gameName = String(game.game_name ?? game.game_identifier ?? identifier)
  const images =
    (game.game_images as string | undefined) ??
    (game.game_image as string | undefined) ??
    (game.image_url as string | undefined)

  return {
    _id: identifier,
    identifier,
    game_name: gameName,
    game_images: images,
    game_provider: String(game.game_provider ?? game.provider_id ?? ''),
    game_slot_status:
      typeof game.game_slot_status === 'boolean' ? game.game_slot_status : undefined,
    openProviderLobby: game.openProviderLobby === true,
    evoCategory:
      typeof game.evoCategory === 'string' && game.evoCategory
        ? game.evoCategory
        : null,
  }
}

/** One-shot navigation — category + openGame so casinoOther auto-launches. */
export function buildCasinoIntLaunchPath(game: Record<string, unknown>): string | null {
  const payload = buildCasinoOpenPayload(game)
  if (!payload) return null

  const routeType = inferCasinoRouteType(game)
  const provider = payload.game_provider?.trim() || '100hp'
  return `/casino-int/${routeType}?category=${encodeURIComponent(provider)}`
}
